import {sleep} from '@/utils'
import {getLocalStorage, setLocalStorage} from '@browser'
import deepmerge from 'deepmerge'
import {watchEffect} from 'vue'
import {useRemoteStream} from '@/composables'
import {messenger} from 'communication/src'

class Speaker {
  constructor() {
    this.virtualSpeaker = document.createElement('audio')
    this.speakers = []
    this.availableSounds = [
      'speaker-test',
    ]
    this.restore()

    const {
      audio: remoteAudioStreams,
    } = useRemoteStream()
    watchEffect(() => {
      const streams = remoteAudioStreams.value || []
      
      // Remove extra speakers if we have more than needed
      while (this.speakers.length > streams.length) {
        const speaker = this.speakers.pop()
        speaker.pause()
        speaker.srcObject = null
      }
      
      // Add or update speakers for each stream
      streams.forEach((stream, index) => {
        if (!this.speakers[index]) {
          const speaker = document.createElement('audio')
          speaker.muted = false
          this.speakers.push(speaker)
          
          // Apply saved device ID if available
          const storage = getLocalStorage('devices')
          if (storage?.speaker?.deviceId && typeof speaker.setSinkId === 'function') {
            speaker.setSinkId(storage.speaker.deviceId).catch(() => {})
          }
        }
        
        if (this.speakers[index].srcObject !== stream) {
          this.speakers[index].srcObject = stream
          this.speakers[index].play()
        }
      })
    })
  }

  restore() {
    const storage = getLocalStorage('devices')
    if (storage?.speaker?.deviceId) {
      const {deviceId} = storage.speaker
      // Runs at module load: a missing API or a stale device id must not break the app.
      // Per-stream speakers get the saved sink when they are created in the watchEffect above
      if (typeof this.virtualSpeaker.setSinkId === 'function') {
        this.virtualSpeaker.setSinkId(deviceId).catch(() => {})
      }
    }
  }

  play(sound) {
    return new Promise((resolve, reject) => {
      const endedHandler = () => {
        resolve()
        this.virtualSpeaker.removeEventListener('ended', endedHandler)
      }
      if (this.availableSounds.includes(sound)) {
        this.virtualSpeaker.src = `media/${sound}.mp3`
        this.virtualSpeaker.addEventListener('ended', endedHandler)
        this.virtualSpeaker.play()
      } else {
        reject(new Error(`Sounds: Unable to play sound ${sound}`))
      }
    })
  }

  async change(deviceId, deviceLabel) {
    messenger.emit('speaker:change', {deviceId})
    
    // Apply to all speakers
    for (const speaker of this.speakers) {
      await speaker.setSinkId(deviceId)
    }
    await this.virtualSpeaker.setSinkId(deviceId)
    await sleep(1000)

    const constraint = {
      speaker: {deviceId, label: deviceLabel},
    }
    const storage = getLocalStorage('devices')
    const update = deepmerge(storage, constraint)
    setLocalStorage('devices', update)
  }
}

const speaker = new Speaker()
export {speaker}
